// Lista de deals CRM (tabular) — /crm/deals (Story 11.4)
// - useInfiniteQuery com filtros (status/stage/responsável/SaaS/origem/valor/parado)
// - Paginação prev/next por cursor (DR-032), reuso de <Pagination>
// - Skeleton no loading inicial (AC10), empty/error states
// - "Dias no estágio" calculado no frontend com helpers de timezone (AC10)
// - Ações won/lost (lost exige motivo) escondidas pra quem não tem permissão (AC8)
//
// Kanban (drag-and-drop) é a story 11.5 — aqui é a visão tabular alternativa.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Briefcase, Check, LayoutGrid, Plus, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';

import type { CrmDealStageSchema, CrmDealStatusSchema } from '@control-center/shared';

import { EmptyState as RichEmptyState } from '../../../components/common/EmptyState';
import { Pagination } from '../../../components/common/Pagination';
import { PillButton } from '../../../components/clients/PillButton';
import { Select } from '../../../components/clients/form-primitives';
import { LostDealDialog } from '../../../components/crm/LostDealDialog';
import { daysInStage, formatDealMoney } from '../../../lib/deal-helpers';
import {
  useCrmDealsInfinite,
  useUpdateCrmDeal,
  type CrmDealsListFilters,
} from '../../../hooks/use-crm-deals';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { extractErrorMessage } from '../../../lib/errors';
import type { CrmDealEntity } from '../../../lib/crm-deals-api';

const STATUS_FILTERS: { value: '' | CrmDealStatusSchema; label: string }[] = [
  { value: '', label: 'Todos os status' },
  { value: 'open', label: 'Em aberto' },
  { value: 'won', label: 'Ganhos' },
  { value: 'lost', label: 'Perdidos' },
];

const STAGE_FILTERS: { value: '' | CrmDealStageSchema; label: string }[] = [
  { value: '', label: 'Todos os estágios' },
  { value: 'novo_lead', label: 'Novo lead' },
  { value: 'qualificacao', label: 'Qualificação' },
  { value: 'diagnostico', label: 'Diagnóstico' },
  { value: 'proposta_enviada', label: 'Proposta enviada' },
  { value: 'negociacao', label: 'Negociação' },
  { value: 'fechamento_ganho', label: 'Fechamento ganho' },
  { value: 'fechamento_perdido', label: 'Fechamento perdido' },
];

const STAGE_LABEL: Record<CrmDealStageSchema, string> = {
  novo_lead: 'Novo lead',
  qualificacao: 'Qualificação',
  diagnostico: 'Diagnóstico',
  proposta_enviada: 'Proposta enviada',
  negociacao: 'Negociação',
  fechamento_ganho: 'Fechamento ganho',
  fechamento_perdido: 'Fechamento perdido',
};

const STATUS_BADGE: Record<CrmDealStatusSchema, string> = {
  open: 'border-line text-muted',
  won: 'border-good/40 text-good',
  lost: 'border-bad/40 text-bad',
};

const STATUS_LABEL: Record<CrmDealStatusSchema, string> = {
  open: 'Em aberto',
  won: 'Ganho',
  lost: 'Perdido',
};

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

export function CrmDealsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canWrite } = useCrmPermissions();

  // Alterna pra o kanban preservando os filtros já presentes na URL (AC10).
  const goToKanban = (): void => {
    const qs = searchParams.toString();
    navigate(qs ? `/crm/deals/kanban?${qs}` : '/crm/deals/kanban');
  };

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | CrmDealStatusSchema>('');
  const [stageFilter, setStageFilter] = useState<'' | CrmDealStageSchema>('');

  const debouncedQuery = useDebouncedValue(query, 300);

  const filters: CrmDealsListFilters = useMemo(() => {
    const f: CrmDealsListFilters = {};
    if (statusFilter) f.status = statusFilter;
    if (stageFilter) f.stage = stageFilter;
    return f;
  }, [statusFilter, stageFilter]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCrmDealsInfinite(filters);

  const [pageIndex, setPageIndex] = useState(0);
  useEffect(() => setPageIndex(0), [filters]);

  const pages = useMemo(() => data?.pages ?? [], [data]);
  const safeIndex = Math.min(pageIndex, Math.max(pages.length - 1, 0));
  const currentPage = pages[safeIndex];
  const allDeals = useMemo(() => currentPage?.items ?? [], [currentPage]);

  // Busca textual no título é client-side (mantém o backend simples — sem ?q).
  const deals = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return allDeals;
    return allDeals.filter((d) => d.title.toLowerCase().includes(q));
  }, [allDeals, debouncedQuery]);

  const total = currentPage?.meta.total ?? null;

  const rangeStart = useMemo(
    () =>
      allDeals.length === 0
        ? 0
        : pages.slice(0, safeIndex).reduce((acc, p) => acc + p.items.length, 0) + 1,
    [pages, safeIndex, allDeals.length],
  );
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + allDeals.length - 1;

  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < pages.length - 1 || Boolean(hasNextPage);

  const goNext = (): void => {
    if (safeIndex < pages.length - 1) {
      setPageIndex(safeIndex + 1);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage().then((res) => {
        if (!res.isError) setPageIndex(safeIndex + 1);
      });
    }
  };

  const hasActiveFilters = !!debouncedQuery || !!statusFilter || !!stageFilter;

  // Ações de fechamento (won/lost) — via PUT /deals/:id com atalho status.
  const markWonOrLost = useUpdateCrmDeal();
  const [lostTarget, setLostTarget] = useState<CrmDealEntity | null>(null);

  const handleWon = (deal: CrmDealEntity): void => {
    markWonOrLost.mutate(
      { id: deal.id, payload: { version: deal.version, status: 'won' } },
      {
        onSuccess: () => toast.success('Deal marcado como ganho.', { description: deal.title }),
        onError: (err) =>
          toast.error('Não conseguimos marcar como ganho.', {
            description: extractErrorMessage(err, 'Tente de novo em instantes.'),
          }),
      },
    );
  };

  const handleLost = (reason: string): void => {
    if (!lostTarget) return;
    markWonOrLost.mutate(
      {
        id: lostTarget.id,
        payload: { version: lostTarget.version, status: 'lost', lost_reason: reason },
      },
      {
        onSuccess: (deal) => {
          toast.success('Deal marcado como perdido.', { description: deal.title });
          setLostTarget(null);
        },
        onError: (err) =>
          toast.error('Não conseguimos marcar como perdido.', {
            description: extractErrorMessage(err, 'Tente de novo em instantes.'),
          }),
      },
    );
  };

  const clearFilters = (): void => {
    setQuery('');
    setStatusFilter('');
    setStageFilter('');
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="m-0 text-[30px] font-bold leading-none tracking-[-0.025em] text-ink">
            Deals
          </h1>
          <p className="mt-[6px] max-w-[560px] text-[13px] leading-[1.5] text-muted">
            As negociações em andamento — cada oportunidade do funil comercial até ganhar ou perder.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PillButton onClick={goToKanban} icon={<LayoutGrid size={14} strokeWidth={2} />}>
            Funil (kanban)
          </PillButton>
          <PillButton
            onClick={() => refetch()}
            icon={
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={isFetching ? 'animate-spin' : undefined}
              />
            }
          >
            Atualizar
          </PillButton>
          {canWrite && (
            <PillButton
              solid
              onClick={() => navigate('/crm/deals/new')}
              icon={<Plus size={14} strokeWidth={2} />}
            >
              Novo deal
            </PillButton>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_200px_200px]">
        <div className="relative">
          <Search
            size={14}
            strokeWidth={2}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por título…"
            aria-label="Buscar deals"
            className="h-10 w-full rounded-[10px] border border-line bg-panel pl-9 pr-3 text-[13px] text-ink placeholder:text-muted-3 outline-none transition-colors focus:border-line-2"
          />
        </div>
        <Select
          aria-label="Filtrar por status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | CrmDealStatusSchema)}
        >
          {STATUS_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filtrar por estágio"
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as '' | CrmDealStageSchema)}
        >
          {STAGE_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <DealTableSkeleton />
      ) : isError ? (
        <ErrorState
          message={extractErrorMessage(error, 'Não conseguimos carregar os deals.')}
          onRetry={() => refetch()}
        />
      ) : deals.length === 0 ? (
        hasActiveFilters ? (
          <FilteredEmpty onClear={clearFilters} />
        ) : (
          <RichEmptyState
            icon={Briefcase}
            title="Sem deals ainda"
            description="Abra o primeiro deal pra acompanhar a negociação até o fechamento — ganho ou perdido."
            bullets={[
              'Responsável, valor e data esperada de fechamento',
              'Estágio do funil com contagem de dias parado',
              'Marque ganho ou perdido com motivo registrado',
            ]}
            primaryAction={
              canWrite
                ? {
                    label: 'Abrir primeiro deal',
                    icon: <Plus size={14} strokeWidth={2} />,
                    onClick: () => navigate('/crm/deals/new'),
                  }
                : undefined
            }
          />
        )
      ) : (
        <>
          <DealTable
            deals={deals}
            canWrite={canWrite}
            onOpen={(deal) => navigate(`/crm/deals/${deal.id}`)}
            onWon={handleWon}
            onLost={(deal) => setLostTarget(deal)}
          />
          <Pagination
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={() => setPageIndex(Math.max(safeIndex - 1, 0))}
            onNext={goNext}
            isLoading={isFetchingNextPage}
            noun="deal"
          />
        </>
      )}

      {lostTarget && (
        <LostDealDialog
          open={!!lostTarget}
          onOpenChange={(open) => {
            if (!open) setLostTarget(null);
          }}
          dealTitle={lostTarget.title}
          isPending={markWonOrLost.isPending}
          onConfirm={handleLost}
        />
      )}
    </div>
  );
}

// =============================================================================
// Tabela
// =============================================================================

interface DealTableProps {
  deals: CrmDealEntity[];
  canWrite: boolean;
  onOpen: (deal: CrmDealEntity) => void;
  onWon: (deal: CrmDealEntity) => void;
  onLost: (deal: CrmDealEntity) => void;
}

function DealTable({ deals, canWrite, onOpen, onWon, onLost }: DealTableProps): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-muted-3">
            <th className="px-4 py-3 font-medium">Título</th>
            <th className="px-4 py-3 font-medium">Estágio</th>
            <th className="px-4 py-3 font-medium">Dias no estágio</th>
            <th className="px-4 py-3 font-medium">Valor</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {deals.map((deal) => (
            <tr
              key={deal.id}
              onClick={() => onOpen(deal)}
              className="cursor-pointer border-b border-line/60 transition-colors last:border-0 hover:bg-panel-2"
            >
              <td className="px-4 py-3 font-medium text-ink">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(deal);
                  }}
                  className="text-left transition-colors hover:text-link"
                >
                  {deal.title}
                </button>
              </td>
              <td className="px-4 py-3 text-muted-2">{STAGE_LABEL[deal.stage]}</td>
              <td className="px-4 py-3 tabular-nums text-muted-2">
                {deal.status === 'open' ? `${daysInStage(deal.stage_entered_at)} d` : '—'}
              </td>
              <td className="px-4 py-3 tabular-nums text-muted">{formatDealMoney(deal.value)}</td>
              <td className="px-4 py-3">
                <span
                  className={`inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[11px] ${STATUS_BADGE[deal.status]}`}
                >
                  {STATUS_LABEL[deal.status]}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {canWrite && deal.status === 'open' && (
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      aria-label={`Marcar deal ${deal.title} como ganho`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onWon(deal);
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-good/10 hover:text-good"
                    >
                      <Check size={14} strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Marcar deal ${deal.title} como perdido`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onLost(deal);
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-bad/10 hover:text-bad"
                    >
                      <X size={14} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DealTableSkeleton(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <div className="flex flex-col divide-y divide-line/60">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-[14px]">
            <div className="h-3 w-44 animate-pulse rounded bg-panel-2" />
            <div className="h-3 w-28 animate-pulse rounded bg-panel-2" />
            <div className="ml-auto h-3 w-16 animate-pulse rounded bg-panel-2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function FilteredEmpty({ onClear }: { onClear: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-line bg-panel py-12 text-center">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Nenhum deal bate com esses filtros.
      </h2>
      <p className="mx-auto mt-1 max-w-[360px] text-[12px] text-muted">
        Tente afrouxar a busca ou limpar os filtros.
      </p>
      <div className="mt-4 flex justify-center">
        <PillButton onClick={onClear}>Limpar filtros</PillButton>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Não conseguimos carregar os deals.
      </h2>
      <p className="mt-1 text-[12px] text-muted">{message}</p>
      <div className="mt-4">
        <PillButton onClick={onRetry} icon={<RefreshCw size={14} strokeWidth={2} />}>
          Tentar de novo
        </PillButton>
      </div>
    </div>
  );
}
